import type { WorkspaceObject } from "./api";
import { forensicRecordCategory } from "@kavoosh/contracts";

export function payloadText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}
export function sourceLabel(item: WorkspaceObject): string {
  const category = forensicRecordCategory(item.kind, item.payload);
  const attributes = item.payload.attributes;
  if (category && attributes && typeof attributes === "object" && !Array.isArray(attributes)) {
    const values = attributes as Record<string, unknown>;
    const label = payloadText(category === "files" ? values.path : values.name);
    if (label) return label;
  }
  for (const key of [
    "title",
    "canonical_label",
    "summary",
    "statement",
    "description",
    "name",
  ])
    if (payloadText(item.payload[key])) return String(item.payload[key]);
  return item.object_id;
}
export function sourceTime(item: WorkspaceObject): string | null {
  for (const key of [
    "occurred_at",
    "observed_at",
    "started_at",
    "event_time",
    "timestamp",
  ]) {
    const value = payloadText(item.payload[key]);
    if (value && Number.isFinite(Date.parse(value))) return value;
  }
  // Creation is ingestion metadata, never an invented event timestamp.
  return null;
}

export function compareSourceTime(a: WorkspaceObject, b: WorkspaceObject): number {
  const aTime = sourceTime(a), bTime = sourceTime(b);
  if (aTime && bTime) return Date.parse(aTime) - Date.parse(bTime) || a.object_id.localeCompare(b.object_id);
  if (aTime) return -1;
  if (bTime) return 1;
  return a.object_id.localeCompare(b.object_id);
}
export function filterObjects(
  objects: WorkspaceObject[],
  query: string,
  kind = "all",
) {
  const q = query.trim().toLocaleLowerCase();
  return objects.filter(
    (item) =>
      (kind === "all" || item.kind === kind) &&
      (!q ||
        `${sourceLabel(item)} ${item.kind} ${item.object_id} ${JSON.stringify(item.payload)}`
          .toLocaleLowerCase()
          .includes(q)),
  );
}
export function relationshipEndpoints(item: WorkspaceObject) {
  if (item.kind !== "relationship") return null;
  const source = payloadText(item.payload.source_entity_id);
  const target = payloadText(item.payload.target_entity_id);
  return source && target && source !== target ? { source, target } : null;
}
export function projectGraph(objects: WorkspaceObject[]) {
  const entities = objects.filter((item) => item.kind === "entity");
  const ids = new Set(entities.map((item) => item.object_id));
  const relationships = objects.filter((item) => {
    const ends = relationshipEndpoints(item);
    return ends && ids.has(ends.source) && ids.has(ends.target);
  });
  return {
    entities,
    relationships,
    unresolved:
      objects.filter((item) => item.kind === "relationship").length -
      relationships.length,
  };
}
export function linkedObjects(objects: WorkspaceObject[], selected: string) {
  const current = objects.find((item) => item.object_id === selected);
  const anchors = new Set([
    selected,
    ...(current?.provenance
      .filter((p) => p.source_type === "forensic_object")
      .map((p) => p.source_id) ?? []),
  ]);
  const adjacent = new Set(anchors);
  for (const item of objects) {
    const ends = relationshipEndpoints(item);
    if (
      ends &&
      (item.object_id === selected ||
        anchors.has(ends.source) ||
        anchors.has(ends.target))
    ) {
      adjacent.add(item.object_id);
      adjacent.add(ends.source);
      adjacent.add(ends.target);
    }
  }
  return objects.filter(
    (item) =>
      adjacent.has(item.object_id) ||
      item.provenance.some(
        (binding) =>
          binding.source_type === "forensic_object" &&
          adjacent.has(binding.source_id),
      ),
  );
}


export type InvestigationMode = "graph" | "timeline" | "processes" | "network" | "files";

/** Match canonical parser types, preserving observations as observations. */
export function recordsForMode(objects: WorkspaceObject[], mode: InvestigationMode) {
  return objects.filter(item => {
    if (mode === "graph" || mode === "timeline") {
      return item.kind === "timeline_event" || item.kind === "observation";
    }
    return forensicRecordCategory(item.kind, item.payload) === mode;
  });
}
